import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Dropdown, Menu, Select, Tag, Typography } from "@arco-design/web-react";
import { IconClose, IconPlus } from "@arco-design/web-react/icon";
import { PostApi } from "../../Api/AuthenticatedApi";
import TermEditor from "./TermEditor";
import {
  AND, FIELDS, OR, addTerm, customFields, fieldOf, forServer, newTerm, removeTerm, termText, updateTerm,
} from "./contactFilter";

const { Text } = Typography;

const OP_CHOICES = [
  { value: AND, label: "all" },
  { value: OR, label: "any" },
];

// AddRuleMenu picks the field for a new rule: the standard ones, then the
// org's custom fields.
function AddRuleMenu({ options, onPick, label = "Add rule" }) {
  const custom = customFields(options.customFieldDefs);
  const menu = (
    <Menu
      style={{ maxHeight: 360, overflowY: "auto" }}
      onClickMenuItem={(key) => {
        if (key.startsWith("cf:")) {
          onPick(newTerm("cf", custom.find((c) => c.def.key === key.slice(3)).def));
        } else {
          onPick(newTerm(key));
        }
      }}
    >
      {FIELDS.map((f) => <Menu.Item key={f.field}>{f.label}</Menu.Item>)}
      {custom.length > 0 && (
        <Menu.ItemGroup title="Custom fields">
          {custom.map((c) => <Menu.Item key={"cf:" + c.def.key}>{c.info.label}</Menu.Item>)}
        </Menu.ItemGroup>
      )}
    </Menu>
  );
  return (
    <Dropdown droplist={menu} trigger="click" position="bl">
      <Button size="small" type="text" icon={<IconPlus />}>{label}</Button>
    </Dropdown>
  );
}

// FilterBuilder edits a filter: groups of rules. Inside a group, "Match all /
// any of these" joins its rules; between groups, one AND / OR joins them all.
export default function FilterBuilder({ value, onChange, options }) {
  const filter = value;
  const multiGroup = filter.groups.length > 1;
  const setGroupOp = (gi, op) =>
    onChange({ ...filter, groups: filter.groups.map((g, i) => (i === gi ? { ...g, op } : g)) });

  return (
    <div data-testid="filter-builder">
      {filter.groups.map((group, gi) => (
        <React.Fragment key={gi}>
          {gi > 0 && (
            <div style={{ display: "flex", justifyContent: "center", margin: "8px 0" }}>
              <Select
                size="small"
                aria-label="Join groups with"
                style={{ width: 90 }}
                value={filter.op}
                onChange={(op) => onChange({ ...filter, op })}
                options={[{ value: AND, label: "AND" }, { value: OR, label: "OR" }]}
              />
            </div>
          )}
          <div style={{ border: "1px solid var(--color-border-2)", borderRadius: 6, padding: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
              <Text>Match</Text>
              <Select
                size="small"
                aria-label={`Group ${gi + 1} matches`}
                style={{ width: 70 }}
                value={group.op || AND}
                onChange={(op) => setGroupOp(gi, op)}
                options={OP_CHOICES}
              />
              <Text>of these rules</Text>
              <div style={{ flex: 1 }} />
              {multiGroup && (
                <Button
                  size="mini"
                  type="text"
                  aria-label={`Remove group ${gi + 1}`}
                  icon={<IconClose />}
                  onClick={() => onChange({ ...filter, groups: filter.groups.filter((_, i) => i !== gi) })}
                />
              )}
            </div>

            {group.terms.length === 0 && (
              <Text type="secondary" style={{ display: "block", fontSize: 12, margin: "4px 0" }}>
                No rules yet — everyone matches.
              </Text>
            )}
            {group.terms.map((term, ti) => {
              const info = fieldOf(term, options.customFieldDefs);
              return (
                <div key={ti} data-testid="filter-term" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, padding: "4px 0" }}>
                  {ti > 0 && <Tag size="small">{(group.op || AND) === OR ? "or" : "and"}</Tag>}
                  <Text style={{ fontWeight: 500, minWidth: 90 }}>{info ? info.label : term.key || term.field}</Text>
                  <TermEditor term={term} options={options} onChange={(t) => onChange(updateTerm(filter, gi, ti, t))} />
                  <Button
                    size="mini"
                    type="text"
                    aria-label={`Remove rule ${termText(term, options.names, options.customFieldDefs)}`}
                    icon={<IconClose />}
                    onClick={() => onChange(removeTerm(filter, gi, ti))}
                  />
                </div>
              );
            })}
            <AddRuleMenu options={options} onPick={(t) => onChange(addTerm(filter, gi, t))} />
          </div>
        </React.Fragment>
      ))}
      <div style={{ marginTop: 8 }}>
        <Button
          size="small"
          type="dashed"
          icon={<IconPlus />}
          onClick={() => onChange({ ...filter, groups: [...filter.groups, { op: AND, terms: [] }] })}
        >
          Add group
        </Button>
      </div>
    </div>
  );
}

// FilterCounts shows how many contacts the filter (plus search text) matches,
// and how many of them a broadcast would reach. Unfinished rules are left out.
export function FilterCounts({ filter, searchTerm, customFieldDefs }) {
  const { getAccessTokenSilently } = useAuth0();
  const [counts, setCounts] = React.useState(null);
  const payload = JSON.stringify({ search_term: searchTerm || "", filter: forServer(filter, customFieldDefs) });

  React.useEffect(() => {
    let live = true;
    const timer = setTimeout(() => {
      PostApi(getAccessTokenSilently, "/api/contacts/count",
        (d) => live && setCounts(d),
        () => {},
        () => live && setCounts({ error: true }),
        JSON.parse(payload));
    }, 300);
    return () => { live = false; clearTimeout(timer); };
  }, [getAccessTokenSilently, payload]);

  if (!counts) return <Text type="secondary">Counting…</Text>;
  if (counts.error) return <Text type="error">Couldn't count the matches.</Text>;
  return (
    <Text data-testid="filter-counts">
      <strong>{(counts.match || 0).toLocaleString()}</strong> match ·{" "}
      <Text type="secondary">{(counts.mailable || 0).toLocaleString()} can be mailed</Text>
    </Text>
  );
}

// FilterChips shows the filter as removable chips, with the and / or words
// between them and brackets around groups.
export function FilterChips({ filter, options, onRemove, onClick }) {
  if (!filter) return null;
  const groups = filter.groups.map((g, gi) => ({ g, gi })).filter(({ g }) => g.terms.length > 0);
  if (!groups.length) return null;
  const word = (op) => (op === OR ? "or" : "and");
  return (
    <div data-testid="filter-chips" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
      {groups.map(({ g, gi }, n) => (
        <React.Fragment key={gi}>
          {n > 0 && <Text style={{ fontWeight: 600, fontSize: 12 }}>{word(filter.op).toUpperCase()}</Text>}
          {groups.length > 1 && g.terms.length > 1 && <Text type="secondary">(</Text>}
          {g.terms.map((t, ti) => (
            <React.Fragment key={ti}>
              {ti > 0 && <Text type="secondary" style={{ fontSize: 12 }}>{word(g.op)}</Text>}
              <Tag
                closable
                color="arcoblue"
                style={{ cursor: onClick ? "pointer" : undefined }}
                onClick={onClick}
                onClose={(e) => { if (e && e.stopPropagation) e.stopPropagation(); onRemove(gi, ti); }}
              >
                {termText(t, options.names, options.customFieldDefs)}
              </Tag>
            </React.Fragment>
          ))}
          {groups.length > 1 && g.terms.length > 1 && <Text type="secondary">)</Text>}
        </React.Fragment>
      ))}
    </div>
  );
}
